import {Component, OnDestroy} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {catchError, debounceTime, filter, map, of, Subscription, switchMap} from 'rxjs';
import {IPlatformToolComponent, IPlatformToolHandler} from '@shared/components/platform-tool/platform-tool-container/platform-tool-component';
import {InvalidateSubject} from '@shared/helpers/invalidate.subject';
import {MagicLinkModel, TabModel} from '@shared/models/platform-tool-models';
import {PlatformCommandResponse} from '@shared/commands/command-base';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {AdminDashboardInsightSettings, AdminDashboardInsightSettingsResponse, AdminDashboardQueryParams} from '@features/admin-panel/models/admin-dashboard.models';
import {AdminDashboardImpactState, AdminDashboardInsightImpact} from '@features/admin-panel/models/admin-dashboard-impact.models';
import {countOf} from '@features/admin-panel/helpers/admin-dashboard-text';
import {buildThresholdsGroup, buildWeightsGroup, differsFromSaved, fieldError, formatInput, fromInput, THRESHOLD_GROUPS, ThresholdField, toInput, WEIGHT_FIELDS, WeightField, weightTotal} from './admin-dashboard-settings-fields';

/** ITP-E-168-06. How long the page waits after the last edit before it asks for a new impact preview. */
export const SETTINGS_IMPACT_DEBOUNCE_MS = 400;

/** The Admin dashboard's settings page ('/admin-panel/dashboard-settings'): prediction thresholds and health-score weights. */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-settings',
  templateUrl: './admin-dashboard-settings.component.html',
  styleUrls: ['./admin-dashboard-settings.component.scss']
})
export class AdminDashboardSettingsComponent implements IPlatformToolComponent, OnDestroy {

  handler: IPlatformToolHandler;
  invalidateSubject: InvalidateSubject;
  magicLinkModel: MagicLinkModel;
  selectedTab: TabModel;

  readonly groups = THRESHOLD_GROUPS;
  readonly weightFields = WEIGHT_FIELDS;
  readonly thresholds: FormGroup = buildThresholdsGroup();
  readonly weights: FormGroup = buildWeightsGroup();
  readonly form = new FormGroup({thresholds: this.thresholds, weights: this.weights});

  loading = false;
  loadError = false;
  canEdit = false;
  saved: AdminDashboardInsightSettings | null = null;
  defaults: AdminDashboardInsightSettings | null = null;
  saving = false;
  error: string | null = null;
  recomputing = false;

  /**
   * ITP-E-168-06. The Impact card's companies: every client company, no filters. The dashboard does not keep its scope
   * or filters once you leave it (admin-dashboard.component.ts: "the scope is not persisted") and this page is its own
   * tool, so there is no current dashboard filter to reuse.
   */
  readonly impactParams: AdminDashboardQueryParams = {scope: 'Clients'};
  /** The latest impact preview; it stays on screen (paused) while the values on screen are invalid. */
  impact: AdminDashboardInsightImpact | null = null;
  impactLoading = false;
  impactError = false;
  /** True when `impact` was made from values that differ from the saved ones. */
  impactEdited = false;
  private readonly previewSubscription: Subscription;

  constructor(private dataService: AdminDashboardDataService) {
    // ITP-E-168-06. Every valid edit becomes a live preview SETTINGS_IMPACT_DEBOUNCE_MS after the last change (no Apply
    // button); a newer edit cancels the preview in flight (switchMap); invalid values pause on the last valid preview.
    // A read-only user's disabled form still previews the saved values.
    this.previewSubscription = this.form.valueChanges.pipe(
      debounceTime(SETTINGS_IMPACT_DEBOUNCE_MS),
      filter(() => !!this.saved && (this.form.valid || this.form.disabled)),
      map(() => ({values: this.currentValues(), edited: this.hasChanges})),
      switchMap(({values, edited}) => {
        this.impactLoading = true;
        this.impactError = false;
        return this.dataService.previewInsightImpact(values, this.impactParams).pipe(
          map(impact => ({impact, edited})),
          catchError(() => of({impact: null as AdminDashboardInsightImpact | null, edited}))
        );
      })
    ).subscribe(({impact, edited}) => {
      this.impactLoading = false;
      if (!impact || impact.ErrorMessage) {
        this.impactError = true;
        return;
      }
      this.impact = impact;
      this.impactEdited = edited;
    });
  }

  ngOnDestroy(): void {
    this.previewSubscription.unsubscribe();
  }

  onLoad(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.loadError = false;
    this.dataService.getInsightSettings().pipe(
      catchError(() => of(null as AdminDashboardInsightSettingsResponse))
    ).subscribe(response => {
      this.loading = false;
      if (!response?.Settings) {
        this.loadError = true;
        return;
      }
      this.saved = response.Settings;
      this.defaults = response.Defaults ?? null;
      this.canEdit = response.CanEdit === true;
      this.resetTo(response.Settings);
    });
  }

  /** Puts `values` on screen (percent fields as whole percent); the form is editable only for an administrator. */
  resetTo(values: AdminDashboardInsightSettings): void {
    THRESHOLD_GROUPS.forEach(group => group.fields.forEach(field =>
      this.thresholds.controls[field.key].setValue(toInput(field.unit, values[field.key]))));
    WEIGHT_FIELDS.forEach(field => this.weights.controls[field.key].setValue(values[field.key]));
    if (this.canEdit) {
      this.form.enable();
    } else {
      this.form.disable();
    }
  }

  /** The fourteen values on screen in the stored units (percent fields back to ratios). */
  currentValues(): AdminDashboardInsightSettings {
    const thresholds = this.thresholds.getRawValue();
    const weights = this.weights.getRawValue();
    const values = {} as AdminDashboardInsightSettings;
    THRESHOLD_GROUPS.forEach(group => group.fields.forEach(field =>
      values[field.key] = fromInput(field.unit, Number(thresholds[field.key]))));
    WEIGHT_FIELDS.forEach(field => values[field.key] = Number(weights[field.key]));
    return values;
  }

  /** True when a value on screen differs from `other`. */
  differsFrom(other: AdminDashboardInsightSettings | null): boolean {
    if (!other) {
      return false;
    }
    const values = this.currentValues();
    return (Object.keys(values) as (keyof AdminDashboardInsightSettings)[]).some(key => values[key] !== other[key]);
  }

  get hasChanges(): boolean {
    return this.differsFrom(this.saved);
  }

  get atDefaults(): boolean {
    return !!this.defaults && !this.differsFrom(this.defaults);
  }

  get canSave(): boolean {
    return this.canEdit && !this.saving && this.form.valid && this.hasChanges;
  }

  save(): void {
    if (!this.canSave) {
      return;
    }
    this.saving = true;
    this.error = null;
    this.dataService.saveInsightSettings(this.currentValues()).subscribe({
      next: response => {
        this.saving = false;
        const message = response?.fieldValues?.['ErrorMessage'];
        if (message) {
          this.error = message;
          return;
        }
        this.load();
      },
      error: () => {
        this.saving = false;
        this.error = 'Could not save. Try again.';
      }
    });
  }

  recompute(): void {
    if (!this.canEdit || this.recomputing || this.hasChanges) {
      return;
    }
    this.recomputing = true;
    this.error = null;
    this.dataService.recomputeInsights().subscribe({
      next: response => {
        this.recomputing = false;
        const message = response?.fieldValues?.['ErrorMessage'];
        if (message) {
          this.error = message;
        }
      },
      error: () => {
        this.recomputing = false;
        this.error = 'Could not recompute. Try again.';
      }
    });
  }

  discard(): void {
    if (this.saved) {
      this.resetTo(this.saved);
    }
    this.error = null;
  }

  resetToDefaults(): void {
    if (this.defaults) {
      this.resetTo(this.defaults);
    }
  }

  /** The line under a threshold: its error, else "Default 30% · range 5–90". */
  fieldMeta(field: ThresholdField): string {
    const error = fieldError(this.thresholds.controls[field.key], field.min, field.max);
    if (error) {
      return error;
    }
    const suffix = field.unit === 'pct' ? '%' : field.unit === 'times' ? '×' : '';
    const dflt = this.defaults ? toInput(field.unit, this.defaults[field.key]) : '';
    return `Default ${dflt}${suffix} · range ${field.min}–${field.max}`;
  }

  get weightsTotal(): number {
    return weightTotal(this.weights.getRawValue());
  }

  /** "Adds up to 100%." or by how much the weights are off. */
  get weightsMessage(): string {
    const total = this.weightsTotal;
    if (total === 100) {
      return 'Adds up to 100%.';
    }
    const fix = total < 100 ? `Add ${100 - total}%` : `Take ${total - 100}% off`;
    return `Weights total ${total}% — they must add up to exactly 100%. ${fix} somewhere before you can save.`;
  }

  /** The line under a weight: its error, else "<detail> · default 25%". */
  weightMeta(field: WeightField): string {
    return fieldError(this.weights.controls[field.key], 0, 100)
      ?? `${field.detail} · default ${this.defaults ? this.defaults[field.key] : ''}%`;
  }

  /** The saved value of a threshold in the editor's units (60 for a stored 0.6); null before the settings load. */
  savedThreshold(field: ThresholdField): number | null {
    return this.saved ? toInput(field.unit, this.saved[field.key]) : null;
  }

  /** ITP-E-156-02. True when the threshold on screen differs from the saved value (a blank field counts as changed). */
  thresholdChanged(field: ThresholdField): boolean {
    return differsFromSaved(this.thresholds.controls[field.key].value, this.savedThreshold(field));
  }

  /** True when the threshold on screen is invalid (its line shows an error). */
  thresholdInvalid(field: ThresholdField): boolean {
    return fieldError(this.thresholds.controls[field.key], field.min, field.max) !== null;
  }

  /** "changed from 30%": the saved value, worded like the Last change line. */
  thresholdChangedFrom(field: ThresholdField): string {
    return `changed from ${formatInput(field.unit, this.savedThreshold(field))}`;
  }

  /** True when the weight on screen differs from the saved weight (a blank field counts as changed). */
  weightChanged(field: WeightField): boolean {
    return differsFromSaved(this.weights.controls[field.key].value, this.saved ? this.saved[field.key] : null);
  }

  /** True when the weight on screen is invalid (blank, fractional or outside 0–100). */
  weightInvalid(field: WeightField): boolean {
    return fieldError(this.weights.controls[field.key], 0, 100) !== null;
  }

  /** "changed from 25%". */
  weightChangedFrom(field: WeightField): string {
    return `changed from ${formatInput('pct', this.saved ? this.saved[field.key] : null)}`;
  }

  /** ITP-E-156-04. The width of a weight's bar, in percent: its value clamped to 0–100 (a blank weight is 0). */
  weightBar(field: WeightField): number {
    return Math.min(100, Math.max(0, Number(this.weights.controls[field.key].value) || 0));
  }

  /** ITP-E-156-05. One line per value on screen that differs from the saved one, worded like the server's Last change line. */
  get unsavedChanges(): string[] {
    const lines: string[] = [];
    THRESHOLD_GROUPS.forEach(group => group.fields.filter(field => this.thresholdChanged(field)).forEach(field =>
      lines.push(`${group.title}: ${field.label.toLowerCase()} ${formatInput(field.unit, this.savedThreshold(field))} → ${formatInput(field.unit, this.thresholds.controls[field.key].value)}`)));
    WEIGHT_FIELDS.filter(field => this.weightChanged(field)).forEach(field =>
      lines.push(`Weight: ${field.label.toLowerCase()} ${formatInput('pct', this.saved ? this.saved[field.key] : null)} → ${formatInput('pct', this.weights.controls[field.key].value)}`));
    return lines;
  }

  /** "Previewing 2 unsaved changes: …. Save to apply them for everyone, or discard." (OCA-E-159 settings page 201-206). */
  get previewText(): string {
    const lines = this.unsavedChanges;
    return `Previewing ${countOf(lines.length, 'unsaved change')}: ${lines.join('; ')}. Save to apply them for everyone, or discard.`;
  }

  /** ITP-E-168-06. Which subtitle the Impact card shows (OCA-E-159 settings/page.tsx 335-342). */
  get impactState(): AdminDashboardImpactState {
    if (this.form.valid || this.form.disabled) {
      return this.hasChanges ? 'edited' : 'unedited';
    }
    return this.impact && this.impactEdited ? 'paused' : 'saved';
  }

  onAfterExecuteNextCommand(nextCommandResponse: PlatformCommandResponse): void {}
  onAfterExecutePreviousCommand(previousCommandResponse: PlatformCommandResponse): void {}
  onBeforeExecuteNextCommand(): boolean { return false; }
  onBeforeExecutePreviousCommand(): boolean { return false; }
}
